import React from 'react';
import { Button } from 'react-bootstrap';

import './BayDialog.scss';

import BayModal from '../BayModal';

// Per-key defaults, NOT a whole-object default.
//
// This used to be `options = { size: 'xl', enableCloseBtn: false }` on the
// parameter. A whole-object default is replaced entirely the moment a caller
// passes anything, so every one of the 14 call sites -- all of which pass at
// least a title -- silently lost `size: 'xl'` and rendered at the default modal
// width. Merging per key means a caller overrides only what it names.
const DEFAULT_OPTIONS = {
	size: 'xl' as const,
	enableCloseBtn: false,
};

// Confirm-button label by variant, when the caller does not supply one.
// Was a three-deep nested ternary; this is the same behaviour as a lookup.
const DEFAULT_CONFIRM_TEXT: Record<string, string> = {
	success: 'Done',
	danger: 'Delete',
};

interface DialogFooterProps {
	proceed: (_value?: boolean) => void;
	cancel: () => void;
	variant?: string;
	loading?: boolean;
	buttonText?: string;
	cancelButtonText?: string;
	hideCancelButton?: boolean;
}

const DialogFooter = ({
	proceed,
	cancel,
	variant,
	loading,
	buttonText,
	cancelButtonText,
	hideCancelButton,
}: DialogFooterProps) => (
	<>
		{(variant !== 'success' || !hideCancelButton) && (
			<Button
				className="bay-dialog-button"
				disabled={loading}
				variant="outline-secondary"
				onClick={() => cancel()}
			>
				{cancelButtonText || 'No'}
			</Button>
		)}

		<Button
			className="bay-dialog-button ms-3"
			disabled={loading}
			variant={variant || 'primary'}
			onClick={() => proceed(true)}
		>
			{buttonText || DEFAULT_CONFIRM_TEXT[variant] || 'Ok'}
		</Button>
	</>
);

interface BayDialogOptions {
	id?: string | number;
	title?: React.ReactNode;
	size?: 'sm' | 'lg' | 'xl';
	enableCloseBtn?: boolean;
	variant?: string;
	loading?: boolean;
	buttonText?: string;
	cancelButtonText?: string;
	hideCancelButton?: boolean;
}

interface BayDialogProps {
	/** Whether the dialog is open. Driven by `bayConfirm`. */
	show?: boolean;
	/** Resolve the confirmation. Driven by `bayConfirm`. */
	proceed?: (_value?: boolean) => void;
	/** Dismiss the confirmation. Driven by `bayConfirm`. */
	cancel?: (_value?: unknown) => void;
	/** Body content. Rendered as children, so a node -- not a string. */
	confirmation?: React.ReactNode;
	options?: BayDialogOptions;
	/** Fired once the close transition has finished, so the host root can unmount. */
	onExited?: () => void;
}

/**
 * The dialog itself. Presentational -- it does not own its own visibility or the
 * promise; `bayConfirm` in ./index.ts drives both.
 *
 * This used to be wrapped in `confirmable()` from `react-confirm`, which injected
 * `show`/`proceed`/`cancel` and owned the `show` state. That dependency is gone
 * (see ./index.ts for why and what replaced it), so those three are now ordinary
 * props and the contract is visible in `BayDialogProps` above rather than hidden
 * in a HOC.
 */
const BayDialog = ({
	show,
	proceed,
	cancel,
	confirmation,
	options,
	onExited,
}: BayDialogProps) => {
	const merged = { ...DEFAULT_OPTIONS, ...options };
	const { id, title, size, enableCloseBtn } = merged;

	return (
		<BayModal
			backdropClassName="bay-confirm-dialog-overlay"
			className="confirm-dialog"
			closeButton={enableCloseBtn}
			footer={
				<DialogFooter {...merged} cancel={cancel} proceed={proceed} />
			}
			id={id || 'bay_confirm_dialog'}
			show={show}
			size={size}
			title={title}
			onExited={onExited}
			onHideFunc={() => cancel()}
		>
			{confirmation}
		</BayModal>
	);
};

export default BayDialog;
